import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface SourceOrder {
  type: "table" | "command";
  number: string;
  entityId: string;
  orderId: string;
  itemCount: number;
  total: number;
}

export function useGroupTickets() {
  const { activeRestaurant } = useRestaurantContext();
  const [isSearching, setIsSearching] = useState(false);
  const [isGrouping, setIsGrouping] = useState(false);

  const findActiveOrder = async (
    type: "table" | "command",
    number: string,
    excludeOrderId?: string
  ): Promise<SourceOrder | null> => {
    if (!activeRestaurant) return null;
    setIsSearching(true);

    try {
      if (type === "table") {
        const { data: table, error: tableError } = await supabase
          .from("tables")
          .select("id, number, current_order_id, status, grouped_to_order_id")
          .eq("restaurant_id", activeRestaurant.id)
          .eq("number", number)
          .eq("is_active", true)
          .maybeSingle();

        if (tableError) throw tableError;
        if (!table) {
          toast.error(`Mesa ${number} não encontrada`);
          return null;
        }
        if (table.grouped_to_order_id) {
          toast.error(`Mesa ${number} já está agrupada em outro pedido`);
          return null;
        }
        if (!table.current_order_id) {
          toast.error(`Mesa ${number} não possui pedido ativo`);
          return null;
        }
        if (table.current_order_id === excludeOrderId) {
          toast.error("Não é possível agrupar a mesma mesa/comanda");
          return null;
        }

        const { data: items } = await supabase
          .from("order_items")
          .select("total_price")
          .eq("order_id", table.current_order_id);

        const total = items?.reduce((s, i) => s + Number(i.total_price), 0) ?? 0;

        return {
          type: "table",
          number: table.number,
          entityId: table.id,
          orderId: table.current_order_id,
          itemCount: items?.length ?? 0,
          total,
        };
      } else {
        const { data: command, error: cmdError } = await supabase
          .from("commands")
          .select("id, number, current_order_id, status, grouped_to_order_id")
          .eq("restaurant_id", activeRestaurant.id)
          .eq("number", number)
          .eq("is_active", true)
          .maybeSingle();

        if (cmdError) throw cmdError;
        if (!command) {
          toast.error(`Comanda ${number} não encontrada`);
          return null;
        }
        if (command.grouped_to_order_id) {
          toast.error(`Comanda ${number} já está agrupada em outro pedido`);
          return null;
        }
        if (!command.current_order_id) {
          toast.error(`Comanda ${number} não possui pedido ativo`);
          return null;
        }
        if (command.current_order_id === excludeOrderId) {
          toast.error("Não é possível agrupar a mesma mesa/comanda");
          return null;
        }

        const { data: items } = await supabase
          .from("order_items")
          .select("total_price")
          .eq("order_id", command.current_order_id);

        const total = items?.reduce((s, i) => s + Number(i.total_price), 0) ?? 0;

        return {
          type: "command",
          number: command.number,
          entityId: command.id,
          orderId: command.current_order_id,
          itemCount: items?.length ?? 0,
          total,
        };
      }
    } catch (error) {
      console.error("Error finding active order:", error);
      toast.error("Erro ao buscar pedido");
      return null;
    } finally {
      setIsSearching(false);
    }
  };

  const groupOrders = async (
    targetOrderId: string,
    sources: SourceOrder[]
  ): Promise<boolean> => {
    if (!activeRestaurant) return false;
    setIsGrouping(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      for (const source of sources) {
        // Get all items from source order
        const { data: sourceItems, error: fetchErr } = await supabase
          .from("order_items")
          .select("id, total_price")
          .eq("order_id", source.orderId);

        if (fetchErr) throw fetchErr;

        // Move all items to target order
        if (sourceItems && sourceItems.length > 0) {
          const { error: moveErr } = await supabase
            .from("order_items")
            .update({ order_id: targetOrderId })
            .eq("order_id", source.orderId);

          if (moveErr) throw moveErr;

          // Log transfers for audit
          for (const item of sourceItems) {
            await supabase.from("item_transfers").insert({
              restaurant_id: activeRestaurant.id,
              order_item_id: item.id,
              from_order_id: source.orderId,
              to_order_id: targetOrderId,
              quantity: 1,
              transferred_by: user.id,
            });
          }
        }

        // Mark source order as grouped (set status to delivered with 0 total)
        await supabase
          .from("orders")
          .update({ status: "delivered" as any, subtotal: 0, total: 0 })
          .eq("id", source.orderId);

        // Mark source table/command as grouped
        if (source.type === "table") {
          await supabase
            .from("tables")
            .update({
              grouped_to_order_id: targetOrderId,
              status: "grouped",
              current_order_id: null,
            } as any)
            .eq("id", source.entityId);
        } else {
          await supabase
            .from("commands")
            .update({
              grouped_to_order_id: targetOrderId,
              status: "grouped",
              current_order_id: null,
            } as any)
            .eq("id", source.entityId);
        }
      }

      // Recalculate target order totals
      const { data: allItems } = await supabase
        .from("order_items")
        .select("total_price")
        .eq("order_id", targetOrderId);

      const newSubtotal = allItems?.reduce((s, i) => s + Number(i.total_price), 0) ?? 0;

      await supabase
        .from("orders")
        .update({ subtotal: newSubtotal, total: newSubtotal })
        .eq("id", targetOrderId);

      toast.success(
        `${sources.length} ${sources.length === 1 ? "conta agrupada" : "contas agrupadas"} com sucesso!`
      );
      return true;
    } catch (error) {
      console.error("Error grouping orders:", error);
      toast.error("Erro ao agrupar contas");
      return false;
    } finally {
      setIsGrouping(false);
    }
  };

  return {
    findActiveOrder,
    groupOrders,
    isSearching,
    isGrouping,
  };
}
